import { Link } from 'react-router-dom';
import type { ScoreGame } from '@shared/types/game';
import { allSeatStats, standings } from '@shared/game-logic';
import { Panel } from '@/components/ui';
import { CountUp } from '@/components/CountUp';
import LazyStandingsChart from '@/components/LazyStandingsChart';
import { seatColor, signed } from '@/lib/labels';

const MEDALS = ['🥇', '🥈', '🥉'];

export default function FinalStandings({ game }: { game: ScoreGame }) {
  const rows = standings(game.rounds, game.seats);
  const stats = allSeatStats(game.rounds, game.seats.length);
  const winners = rows.filter((row) => row.placement === 1);

  // Small awards, purely for fun at the end of a game.
  const sharpest = [...stats].sort((a, b) => b.exactPct - a.exactPct)[0];
  const boldest = [...stats].sort((a, b) => b.avgBid - a.avgBid)[0];
  const biggestRound = [...stats].sort((a, b) => (b.bestRound ?? -99) - (a.bestRound ?? -99))[0];

  return (
    <div className="space-y-4">
      <Panel title="Eindstand">
        <p className="mb-4 text-center text-lg">
          {winners.length === 1 ? (
            <>
              🎉 <strong>{winners[0].displayName}</strong> wint met {winners[0].total} punten
            </>
          ) : (
            <>
              🎉 Gedeelde winst voor{' '}
              <strong>{winners.map((winner) => winner.displayName).join(' en ')}</strong> met{' '}
              {winners[0].total} punten
            </>
          )}
        </p>

        <ol className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.seat}
              className="animate-deal-in flex items-center gap-3 rounded-xl bg-base-900/50 px-3 py-2.5"
              style={{ animationDelay: `${row.placement * 90}ms` }}
            >
              <span className="w-6 text-center text-lg">
                {MEDALS[row.placement - 1] ?? (
                  <span className="text-sm text-slate-500">{row.placement}</span>
                )}
              </span>
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: seatColor(row.seat) }}
              />
              <span className="min-w-0 flex-1 truncate font-medium">{row.displayName}</span>
              <CountUp value={row.total} className="text-lg font-bold tabular-nums" />
            </li>
          ))}
        </ol>
      </Panel>

      <Panel title="Onderscheidingen">
        <ul className="space-y-2 text-sm">
          <Award
            emoji="🎯"
            title="Scherpste bieder"
            name={game.seats[sharpest.seat].displayName}
            detail={`${sharpest.exact} van ${sharpest.roundsScored} rondes precies goed`}
          />
          <Award
            emoji="🔥"
            title="Meest gedurfd"
            name={game.seats[boldest.seat].displayName}
            detail={`gemiddeld ${boldest.avgBid.toFixed(1)} slag per ronde geboden`}
          />
          {biggestRound.bestRound !== null && (
            <Award
              emoji="💥"
              title="Beste ronde"
              name={game.seats[biggestRound.seat].displayName}
              detail={`${signed(biggestRound.bestRound)} in één ronde`}
            />
          )}
        </ul>
      </Panel>

      <Panel title="Verloop van de stand">
        <LazyStandingsChart game={game} />
      </Panel>

      <Link
        to="/score/nieuw"
        className="block rounded-2xl bg-accent-500 px-5 py-4 text-center text-lg font-semibold text-base-950 hover:bg-accent-400"
      >
        Nog een potje
      </Link>
      <p className="text-center text-sm text-slate-500">
        Een ronde verkeerd ingevoerd? Ga naar het tabblad Stand en tik de ronde aan.
      </p>
    </div>
  );
}

function Award({
  emoji,
  title,
  name,
  detail,
}: {
  emoji: string;
  title: string;
  name: string;
  detail: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-lg">{emoji}</span>
      <span>
        <span className="block font-medium">
          {title}: {name}
        </span>
        <span className="block text-slate-400">{detail}</span>
      </span>
    </li>
  );
}
